<template>
  <div>
    <div class="is-flex is-justify-content-space-between">
      <label class="label">Contact Person</label>
      <button class="button is-small is-primary is-light"
        :class="`is-light-btn-${$theme.name}`"
        @click="addNewContact">
        <span class="icon">
          <span class="mso mso-24px">
            add
          </span>
        </span>
      </button>
      <ContactModal v-if="userContact"
        :active="showContactModal"
        :contact="userContact"
        @close="showContactModal=false"
        @save="addContact" />
    </div>
    <div v-if="contacts && contacts?.length > 0">
      <div class="field columns mb-0">
        <div class="column is-half">
          <label class="label has-text-weight-normal">First Name</label>
        </div>
        <div class="column is-half">
          <label class="label has-text-weight-normal">Last Name</label>
        </div>
      </div>

      <div v-for="(contact, index) in contacts"
        :key="index"
        class="field columns is-flex is-align-items-center">
        <div class="column is-half">
          <div class="control">
            <input class="input"
              type="text"
              placeholder="First Name"
              v-model="contact.contact.firstName"
              :class="{ 'is-danger' : $v.contacts.$each[index].contact.firstName.$error }"
              @input="$v.contacts.$each[index].contact.firstName.$touch()">
            <span class="help is-danger"
              v-if="!$v.contacts.$each[index].contact.firstName.required">First Name is required
            </span>
          </div>
        </div>
        <div class="field has-addons column is-half">
          <div class="control is-expanded">
            <input class="input"
              type="text"
              placeholder="Last Name"
              v-model="contact.contact.lastName"
              :class="{ 'is-danger' : $v.contacts.$each[index].contact.lastName.$error }"
              @input="$v.contacts.$each[index].contact.lastName.$touch()">
            <span class="help is-danger"
              v-if="!$v.contacts.$each[index].contact.lastName.required">Last Name is required
            </span>
          </div>
          <div class="control">
            <button class="button has-text-gold"
              @click="setDefault(contact, 'contacts')">
              <span class="icon">
                <span class="mso mso-24px"
                  :class="[contact.isDefault ? 'mso-fill': '']">
                  star
                </span>
              </span>
            </button>
          </div>
          <div class="control">
            <button class="button has-text-primary"
              @click="editContact(contact, index)">
              <span class="icon">
                <span class="mso mso-24px"
                  :class="[contact.isDefault ? 'mso-fill': '']">
                  edit
                </span>
              </span>
            </button>
          </div>
          <div class="control">
            <button class="button has-text-danger"
              @click="removeContact(contact)">
              <span class="icon">
                <span class="mso mso-24px">
                  remove
                </span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-else
      class="label has-text-weight-normal ml-1">No Contacts</div>
  </div>
</template>

<script>
import { AddressModel, EmailModel, PhoneModel } from '../../../classes/viewmodels/index.js'
import Guid from '../../../components/Guid.js'
import ContactService from '../../contact/ContactService.js'
import UserValidation from '../UserValidation.js'
import ContactModal from './ContactModal.vue'

export default {
  name: 'UserContacts',
  components: {
    ContactModal
  },
  props: {
    contacts: {
      type: Array,
      default: () => []
    },
    companyId: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      showContactModal: false,
      userContact: null,
      editingContactIndex: null
    }
  },
  computed: {},

  mounted() {
    this.$v.$touch()
  },

  methods: {
    addContact(contact) {
      if (this.editingContactIndex !== null) {
        this.$set(this.contacts, this.editingContactIndex, contact)
        this.editingContactIndex = null
        this.$emit('updateContacts', [...this.contacts])
      } else {
        contact.isNew = true
        this.$emit('addContact', contact)
      }
      this.showContactModal = false
    },
    editContact(contact, index) {
      this.userContact = JSON.parse(JSON.stringify(contact))
      this.editingContactIndex = index
      this.showContactModal = true
    },
    removeContact(contact) {
      this.$emit('removeContact', contact)
    },
    setDefault(contact) {
      this.$emit('setDefault', contact, 'contacts')

      if (Array.isArray(this.contacts)) {
        this.contacts.forEach((c) => {
          if (c === contact && c.contact) {
            if (Array.isArray(c.contact.emails)) {
              c.contact.emails.forEach((email, idx) => {
                email.isDefault = idx === 0
              })
            }
            if (Array.isArray(c.contact.addresses)) {
              c.contact.addresses.forEach((address, idx) => {
                address.isDefault = idx === 0
              })
            }
            if (Array.isArray(c.contact.phones)) {
              c.contact.phones.forEach((phone, idx) => {
                phone.isDefault = idx === 0
              })
            }
          } else if (c.contact) {
            if (Array.isArray(c.contact.emails)) {
              c.contact.emails.forEach((email) => {
                email.isDefault = false
              })
            }
            if (Array.isArray(c.contact.addresses)) {
              c.contact.addresses.forEach((address) => {
                address.isDefault = false
              })
            }
            if (Array.isArray(c.contact.phones)) {
              c.contact.phones.forEach((phone) => {
                phone.isDefault = false
              })
            }
          }
        })
      }
    },
    addNewContact() {
      ContactService.getNewAssetContact('User Profile', Guid.newGuid()).then((response) => {
        const newMap = response.data
        newMap.isNew = true
        newMap.contact.isNew = true
        newMap.contact.addresses = [new AddressModel(Guid.empty())]
        newMap.contact.phones = [new PhoneModel()]
        newMap.contact.emails = [new EmailModel()]
        if (this.$parent && this.$parent.innerValue && this.$parent.innerValue.profile && this.contacts.length == 0) {
          if (this.$parent.innerValue.profile.salutation) {
            newMap.contact.title = this.$parent.innerValue.profile.salutation
          }
          if (this.$parent.innerValue.profile.firstName) {
            newMap.contact.firstName = this.$parent.innerValue.profile.firstName
          }
          if (this.$parent.innerValue.profile.lastName) {
            newMap.contact.lastName = this.$parent.innerValue.profile.lastName
          }
          if (Array.isArray(this.$parent.innerValue.profile.assetAddresses)) {
            const defaultAddress = this.$parent.innerValue.profile.assetAddresses.find((a) => a.isDefault)
            if (defaultAddress) {
              newMap.contact.addresses = [JSON.parse(JSON.stringify(defaultAddress))]
              newMap.contact.addresses[0].isDefault = true
              newMap.contact.addresses[0].isNew = true
              newMap.contact.addresses[0].id = Guid.empty()
            }
          }
          if (Array.isArray(this.$parent.innerValue.profile.assetPhones)) {
            const defaultPhone = this.$parent.innerValue.profile.assetPhones.find((a) => a.isDefault)
            if (defaultPhone) {
              newMap.contact.phones = [JSON.parse(JSON.stringify(defaultPhone))]
              newMap.contact.phones[0].isDefault = true
              newMap.contact.phones[0].isNew = true
              newMap.contact.phones[0].id = Guid.empty()
            }
          }
          if (Array.isArray(this.$parent.innerValue.profile.assetEmails)) {
            const defaultEmail = this.$parent.innerValue.profile.assetEmails.find((a) => a.isDefault)
            if (defaultEmail) {
              newMap.contact.emails = [JSON.parse(JSON.stringify(defaultEmail))]
              newMap.contact.emails[0].isDefault = true
              newMap.contact.emails[0].isNew = true
              newMap.contact.emails[0].id = Guid.empty()
            }
          }
        }

        var anyDefault = this.contacts.find((d) => !d.isDeleted && d.isDefault)
        if (!anyDefault) {
          newMap.isDefault = true
        }
        this.userContact = newMap
        this.userContact.companyId = this.companyId
      })
      this.showContactModal = true
    },
    addTmpAddress() {
      let newAddress = new AddressModel(this.companyId)
      if (this.userContact.contact.addresses.length == 0) {
        newAddress.isDefault = true
      }
      this.userContact.contact.addresses.push(newAddress)
    },
    removeAddress(address) {
      this.userContact.contact.addresses = this.userContact.contact.addresses.filter((a) => a !== address)
    }
  },

  validations: {
    ...UserValidation.validations.entity
  }
}
</script>

<style lang="scss" scoped>
.is-invalid {
  background-color: pink !important;
}
</style>
